import React, { useEffect, useRef, useState, useMemo } from "react";
import Image from "next/image";
import Breadcrumb from "@/components/brandRefresh/common/Breadcrumb";
import { useRouter } from "next/router";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { ClearIcon } from "@mui/x-date-pickers";

const useSearchWithBreadCrumbs = ({
  breadcrumbTree,
  defaultValues,
  searchFilterCallback,
  showAutoCompleteDropDown = true,
}) => {
  const router = useRouter();

  const [search, setSearch] = useState("");
  const [filteredValues, setFilteredValues] = useState(defaultValues);

  const [showClose, setShowClose] = useState(false);

  const [mobileSearchVisible, setMobileSearchVisible] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    // Check if there's a search query in the URL
    if (router.query?.search) {
      let searchTerm = decodeURIComponent(router.query.search);
      if (searchTerm.trim() !== "") {
        searchTerm = searchTerm.replace(/\/$/, "");
        setSearch(searchTerm);
      } else {
        setSearch("");
      }
    }
  }, [router.query.search]);

  useEffect(() => {
    if (search.length) {
      const filteredData = searchFilterCallback(defaultValues, search);
      setFilteredValues(filteredData);
    } else {
      setFilteredValues(defaultValues);
    }
  }, [search, defaultValues]);

  // Focus on the input when mobileSearchVisible becomes true
  useEffect(() => {
    if (mobileSearchVisible && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [mobileSearchVisible]);

  const renderedSearchSuggestions = useMemo(
    () =>
      showAutoCompleteDropDown && search.length
        ? filteredValues?.map(({ Question, slug, id, excludeParams }) => ({
            label:
              Question.length > 32 ? `${Question.slice(0, 32)}...` : Question,
            slug,
            id,
            excludeParams,
            Question,
          }))
        : [],
    [filteredValues],
  );

  const renderSearchView = () => (
    <div className="text-sm md:text-base">
      <div className="flex justify-between items-center">
        {!mobileSearchVisible && <Breadcrumb breadcrumbTree={breadcrumbTree} />}
        <div
          className={`text-onmoPrimary w-auto md:w-64 relative md:text-black md:bg-onmoPurple-50 flex items-center rounded-md ${
            mobileSearchVisible && "!w-full bg-onmoPurple-50 p-2"
          }`}
        >
          <div
            className={`h-8 w-8 m-1 md:items-center md:justify-center ${
              showClose ? "md:hidden" : "md:flex"
            } hidden`}
          >
            <Image
              height={18}
              width={18}
              src="/images/icons/search_icon_grey.svg"
              alt="search icon"
              className={` text-onmoGrey`}
            />
          </div>

          {!mobileSearchVisible && (
            <div className="md:hidden h-6 w-6 flex items-center justify-center">
              <Image
                height={18}
                width={18}
                src="/images/icons/search_icon_purple.svg"
                alt="search icon"
                onClick={() => {
                  setMobileSearchVisible(true);
                  if (searchInputRef.current) {
                    searchInputRef.current.focus();
                  }
                }}
              />
            </div>
          )}

          <Autocomplete
            value={search}
            fullWidth
            className={` ${mobileSearchVisible ? "block" : "hidden md:block"}`}
            componentsProps={{
              paper: {
                sx: {
                  "@media (min-width:600px)": {
                    width: 320,
                  },
                  margin: "5px 0",
                  backgroundColor: "#F4F3FF",
                },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "0",
                paddingRight: "10px !important",
                "&:focusVisible": { outline: "none" },
              },

              "& .MuiInputBase-input": {
                padding: "0", // Remove padding for input itself
              },
              "& .MuiAutocomplete-inputFocused": {
                "&:focusVisible": { outline: "none" },
                borderRadius: "0",
                padding: "0",
              },
              "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                border: "0px solid #eee",
                paddingRight: "10px",
              },
              "&:focusVisible": { outline: "none" },
              // display: mobileSearchVisible ?'':'none'
            }}
            autoFocus
            filterOptions={options => options} // Disable default filtering
            options={renderedSearchSuggestions}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                autoFocus
                placeholder="Search for a term..."
                onChange={event => {
                  setSearch(event.target.value);
                  setShowClose(true);
                }}
                className="bg-onmoPurple-50 outline-none rounded-md text-black relative"
                sx={{
                  height: "40px",
                  width: "100%",
                  paddingRight: "0px",
                  "& .MuiOutlinedInput-root": {
                    paddingRight: "10px",
                    maxHeight: "40px",
                    placeContent: "center",
                    "&:focusVisible": { outline: "none" },
                    "& fieldset": {
                      border: "none",
                    },
                    "&.Mui-focused fieldset": {
                      border: "none",
                    },
                    "&:hover fieldset": {
                      border: "none",
                    },
                  },
                  "& .MuiInputBase-input": {
                    padding: "0",
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment:
                    showClose && search ? (
                      <ClearIcon
                        sx={{ cursor: "pointer" }}
                        className="text-onmoPrimary"
                        onClick={() => {
                          setSearch("");
                          setShowClose(false);
                          setMobileSearchVisible(false);
                          setFilteredValues([]);
                          router.push({
                            pathname: window.location.pathname,
                            query: "",
                          });
                        }}
                      />
                    ) : null,
                }}
                inputRef={searchInputRef}
              />
            )}
            onChange={(event, value) => {
              if (value) {
                const { slug, excludeParams, Question } =
                  renderedSearchSuggestions.find(
                    suggestion => suggestion.id === value.id,
                  );
                router.push({
                  pathname: `/help/faq/${slug}`,
                  query: !excludeParams && {
                    search: Question,
                  },
                });
              }
            }}
            freeSolo
            noOptionsText="No suggestions"
          />
        </div>
      </div>
      {search?.length && !showAutoCompleteDropDown ? (
        <div className="my-6 text-onmoGrey-medium break-all">
          {filteredValues?.length > 0 ? (
            <p>{`Search results  for "${search}"`}</p>
          ) : (
            <p>{`No results found for "${search}"`}</p>
          )}
        </div>
      ) : (
        <div className="m-[52px] lg:m-[72px]" />
      )}
    </div>
  );

  return { filteredValues, renderSearchView };
};

export default useSearchWithBreadCrumbs;
